import { PWAUpdateService } from './shared/services/PWAUpdateService';
import { InstallService } from './shared/services/InstallService';
import { FeatureSwitchService } from './shared/services/FeatureSwitch.service';
import { AuthModule } from './auth/auth.module';
import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ProPoolApp } from './app.component';
import { AuthFacade } from './auth/state/auth.facade';
import { StorageService } from './shared/services/storage.service';
import { SignalRService } from './shared/services/signalR.service';
import { OneSignal } from '@ionic-native/onesignal/ngx';
import { PushService } from './shared/services/PushService';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { ScreenOrientationService } from './shared/services/ScreenOrientationService';
import { DiagnosticService } from './shared/services/DiagnosticService';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { AppSettingsService } from './shared/services/appsettings.service';
import { AppRoutingModule } from './app-routing.module';
import { RouteReuseStrategy } from '@angular/router';
import { IonicRouteStrategy, IonicModule } from '@ionic/angular';
import { InputValidator } from './shared/helpers/inputvalidator';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { AlertService } from './shared/services/alert.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { SharedModule } from './shared/shared.module';
import { AppEffects } from './shared/states/app.effects';
import { HttpErrorInterceptor } from './shared/interceptors/httperror.interceptor';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { DarkModeService } from './shared/services/DarkMode.service';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { environment } from '../environments/environment';
import { PlayersHttpService } from './players/services/players.httpservice';
import { StatisticsHttpService } from './shared/services/statistics.httpservice';
import { NotificationsHttpService } from './shared/services/notifications.httpservice';
import { MatchUtilitiesService } from './matches/services/match-utilities.service';
import { MatchesHttpService } from './matches/services/matches.httpservice';
import { PlaceUtilitiesService } from './places/services/place.utilities.service';
import { TeamsHttpService } from './teams/services/teams.httpservice';
import { LeaguesHttpService } from './leagues/services/leagues.httpservice';
import { BadgesHttpService } from './shared/services/badges.httpservice';

registerLocaleData(localeNl);

@NgModule({
  declarations: [
    ProPoolApp
  ],
  imports: [
    BrowserModule,
    SharedModule,
    AppRoutingModule,
    HttpClientModule,
    IonicModule.forRoot({
      mode: 'ios',
      backButtonText: 'Terug'
    }),
    IonicStorageModule.forRoot({
      name: '__propool',
      // Pinned to a single driver (no IndexedDB/WebSQL fallback chain): Chrome DevTools' predefined
      // device presets (e.g. "iPhone") restrict IndexedDB, which makes localForage silently switch
      // drivers underneath us — landing on a separate storage backend with old/no data, which looks
      // like "logged in as someone else". See https://stackoverflow.com/q/50369826.
      driverOrder: [Drivers.LocalStorage]
    }),
    AuthModule,
    StoreModule.forRoot({}, {
        // Dev-only: these deep-clone/compare state and actions on every dispatch, so they're not meant
        // to run in production. Verified safe to enable via a real dispatch test against the tournaments
        // and places reducers/actions (including the deeply-nested SignalR sync actions) plus a sweep of
        // every reducer for direct-mutation patterns - this codebase's actions/state are built from plain
        // object literals throughout (even where the TypeScript type is a class), not real class
        // instances, so serializability holds too.
        runtimeChecks: {
			strictStateImmutability: !environment.production,
			strictActionImmutability: !environment.production,
			strictStateSerializability: !environment.production,
			strictActionSerializability: !environment.production
        }
    }),
    EffectsModule.forRoot([AppEffects]),
    StoreDevtoolsModule.instrument(),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production })
  ],
  providers: [
    OneSignal,
    Diagnostic,
    PushService,
    StorageService,
    ScreenOrientation,
    ScreenOrientationService,
    AuthFacade,
    SignalRService,
    DiagnosticService,
    AppSettingsService,
    InputValidator,
    AlertService,
    DarkModeService,
    FeatureSwitchService,
    SwUpdate,
    PWAUpdateService,
    InstallService,
    // Root-provided (rather than scoped to PlayersModule like the other feature modules' HTTP
    // services) so PlayerResolver can also be providedIn: 'root' and be used by other lazy feature
    // modules (e.g. matches' create-match route) without importing the whole PlayersModule just for
    // its resolver - see TODO.md.
    PlayersHttpService,
    // Root-provided for the same reason as PlayersHttpService above: statistics/head-to-head data is
    // shown from both the players feature (player detail page) and the profile feature (statistics
    // page), so it can't live scoped to either module's own providers.
    StatisticsHttpService,
    // Root-provided so the profile page's notification bell can use it without pulling in a feature
    // module just for this one popover.
    NotificationsHttpService,
    // MatchUtilitiesService depends on PlayerUtilitiesService, already root-available via AuthModule
    // (imported eagerly above) - not re-provided here.
    MatchUtilitiesService,
    // PlaceUtilitiesService: root-provided so MatchesHttpService (also root, see below) can populate
    // a match's Place without pulling in PlacesModule.
    PlaceUtilitiesService,
    // Root-provided for the same reason as PlayersHttpService above: the profile page's "next match"
    // card needs match data too, without pulling in the whole MatchesModule (and its own 'matches'
    // ngrx feature state, which - unlike this plain HTTP service - can't safely be shared this way,
    // since it's only registered once MatchesModule itself loads).
    MatchesHttpService,
    // Root-provided for the same reason as PlayersHttpService above: the profile page's favorite
    // teams/invites cards need TeamsHttpService without pulling in TeamsModule.
    TeamsHttpService,
    LeaguesHttpService,
    // Root-provided so the profile page's badges card can use it without pulling in a feature module.
    BadgesHttpService,
    { provide: LOCALE_ID, useValue: "nl-NL" },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true }
  ],
  bootstrap: [ProPoolApp]
})
export class AppModule {

}
